import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/base/VideoCard';
import ArticleCard from '@/components/base/ArticleCard';
import { videos, articles } from '@/mocks/data';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || '1', 10);
  const article = articles.find((a) => a.id === articleId) || articles[0];

  const relatedVideos = videos
    .filter((v) => v.categorySlug === article.categorySlug)
    .slice(0, 4);

  const relatedArticles = articles
    .filter((a) => a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': article.title,
    'description': article.summary,
    'author': {
      '@type': 'Person',
      'name': article.author,
    },
    'datePublished': article.date,
    'image': article.image,
    'publisher': {
      '@type': 'Organization',
      'name': '豆花视频',
      'url': 'https://www.douhuavideo.com',
    },
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': `https://www.douhuavideo.com/article/${article.id}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      {
        '@type': 'ListItem',
        'position': 1,
        'name': '首页',
        'item': 'https://www.douhuavideo.com/',
      },
      {
        '@type': 'ListItem',
        'position': 2,
        'name': article.category,
        'item': `https://www.douhuavideo.com/category/${article.categorySlug}`,
      },
      {
        '@type': 'ListItem',
        'position': 3,
        'name': article.title,
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: article.category, href: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />
          </div>

          {/* Article Header */}
          <article className="px-4 md:px-6 py-8">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-2 mb-4">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="text-xs px-3 py-1 bg-primary-100 text-primary-700 rounded-full font-medium whitespace-nowrap hover:bg-primary-200 transition-colors cursor-pointer"
                >
                  {article.category}
                </Link>
                <span className="text-xs text-foreground-400">{article.date}</span>
                <span className="text-xs text-foreground-400 ml-auto flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-eye-line"></i>
                  </div>
                  {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万次阅读` : `${article.views}次阅读`}
                </span>
              </div>

              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4 font-heading">
                {article.title}
              </h1>

              <div className="flex items-center gap-3 mb-6 pb-6 border-b border-background-200">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-500 font-bold text-sm">
                  {article.author.charAt(0)}
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-800">{article.author}</p>
                  <p className="text-xs text-foreground-400">发布于 {article.date}</p>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg mb-8">
                <img
                  src={article.image}
                  alt={article.title}
                  title={article.title}
                  className="w-full h-auto object-cover"
                />
              </div>

              <div
                className="prose prose-lg max-w-none text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="flex items-center flex-wrap gap-2 mt-8 pt-6 border-t border-background-200">
                <span className="text-sm text-foreground-500 mr-2">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-3 py-1 bg-background-100 text-foreground-600 rounded-full whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </article>

          {/* Related Videos */}
          {relatedVideos.length > 0 && (
            <section className="px-4 md:px-6 py-10 bg-background-100">
              <div className="max-w-7xl mx-auto">
                <h2 className="text-xl font-bold text-foreground-900 mb-5 font-heading">
                  相关视频
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {relatedVideos.map((video) => (
                    <VideoCard key={video.id} {...video} />
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="px-4 md:px-6 py-10">
              <div className="max-w-7xl mx-auto">
                <h2 className="text-xl font-bold text-foreground-900 mb-5 font-heading">
                  相关推荐
                </h2>
                <div className="space-y-4">
                  {relatedArticles.map((a) => (
                    <ArticleCard key={a.id} {...a} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}